import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MapView from "./components/MapView";
import GoogleMapView from "./components/GoogleMapView";
import ListGrid from "./components/ListGrid";
import Pagination from "./components/Pagination";
import { getHostEl } from "./utils/hostElements";
import { readBoolAttr, readIntAttr, truthy, currencySymbol, normalizeImageSize, pickImageByPref, normalizeMapStyle } from "./utils/helpers";
import { applyColsToVars } from "./utils/columns";
import { buildCss } from "./utils/cssBuilder";
import { injectCss, injectCustomCss, PREVIEW_CSS_TAG_ID } from "./utils/cssInjector";
import { ICON_ATTRS, mapStyleToIconSpec, applyIconSpecToEl } from "./utils/iconHelpers";
import { offsetTopRelative, animateScroll } from "./utils/scrollHelpers";
import { logger } from "../shared/logger";
// Note: We DON'T use getCachedAccount - grid widget fetches fresh data from API on every load

export default function Widget({
  columns = { base: 1, md: 3, lg: 4 },
  columnsNoMap = null,
  columnsWithMap = null,
  gap = 12,
  radius = 12,
  shadow = true,
  map = true,
  mapHeight = 520,
  mapHeightUnit = "px",
  isGoogle = false,
  googleApiKey = "",
  isPricePins = false,
  enableClustering = false,
  clusterStyle = {
    bgColor: "#fe5922",
    textColor: "#ffffff",
    fontSize: 14,
    fontWeight: 700,
    size: 40,
  },
  customSvgUrl = "",
  apiUrl = "https://api.nexus.design/api/listings/search-availability",
  authToken = "",
  account = "",
  perPage = 24,
  debounceMs = 400,
  currentQuery = "listing",
  mapShowDesktop = true,
  mapShowTablet = false,
  mapShowMobile = false,
  previewWidth = null, // For dashboard preview mode (e.g., "375px", "768px", "1024px")
  // Map settings
  mapWheel = true,
  mapZoom = 10,
  mapCenter = { lat: 51.5072, lng: -0.1276 },
  // Link settings
  linkTarget = "_self",
  // Metadata display
  showGuests = true,
  showBedrooms = true,
  showBathrooms = true,
  showLocation = true,
  // Price text
  singularPrefix = "",
  singularSuffix = "",
  pluralPrefix = "",
  pluralSuffix = "",
  // Embedded Search Bar
  searchBarEnabled = true,
  searchBarSticky = false,
  searchBarPadding = 0,
  searchBarBorderWidth = 0,
  searchBarBorderColor = "#e5e7eb",
  searchBarScriptUrl = "/search/nexus-search.iife.js",
  // Display options
  lazyLoad = true,
  virtualScroll = false,
  showTags = true,
  showAmenities = true,
}) {
  // ---- icon spec
  const [iconSpec, setIconSpec] = useState({
    mode: "fa",
    faFamily: "fa-solid",
    faIcon: "fa-location-dot",
    size: 28,
    color: "#fe5922",
    svg: "",
  });

  const [imagePref, setImagePref] = useState("md");
  const [savedTextMeta, setSavedTextMeta] = useState({});
  const [savedPerPage, setSavedPerPage] = useState(null);
  const [mapTileStyle, setMapTileStyle] = useState(undefined);
  const [cardShadowCss, setCardShadowCss] = useState(
    shadow ? "0 6px 20px rgba(2,6,23,.08)" : "none"
  );
  const [selectedCardAmenities, setSelectedCardAmenities] = useState([]);
  const [selectedCardTags, setSelectedCardTags] = useState([]);

  // Search bar state (can be overridden by saved settings)
  const [searchBarLoaded, setSearchBarLoaded] = useState(false);
  const [searchBarEnabledLocal, setSearchBarEnabledLocal] = useState(searchBarEnabled);
  const [searchBarStickyLocal, setSearchBarStickyLocal] = useState(searchBarSticky);
  const [searchBarPaddingLocal, setSearchBarPaddingLocal] = useState(searchBarPadding);
  const [searchBarBorderWidthLocal, setSearchBarBorderWidthLocal] = useState(searchBarBorderWidth);
  const [searchBarBorderColorLocal, setSearchBarBorderColorLocal] = useState(searchBarBorderColor);
  const searchBarRef = useRef(null);

  // Display options (can be overridden by saved settings)
  const [lazyLoadLocal, setLazyLoadLocal] = useState(lazyLoad);
  const [showTagsLocal, setShowTagsLocal] = useState(showTags);
  const [showAmenitiesLocal, setShowAmenitiesLocal] = useState(showAmenities);

  // Location/City hero section state
  const [locationData, setLocationData] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  // local fallback for provider/key (if entry.jsx didn't pass)
  const [isGoogleLocal, setIsGoogleLocal] = useState(isGoogle);
  const [googleApiKeyLocal, setGoogleApiKeyLocal] = useState(googleApiKey);

  // local map height (can be overridden by saved settings)
  const [mapHeightLocal, setMapHeightLocal] = useState(mapHeight);
  const [mapHeightUnitLocal, setMapHeightUnitLocal] = useState(mapHeightUnit);

  const getHostEl = () =>
    document.querySelector("#nexus-grid-widget") ||
    document.querySelector(".nexus-grid-widget") ||
    document.querySelector("[data-nexus-grid]");

  // URL state
  const [queryString, setQueryString] = useState(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const sp = useMemo(() => new URLSearchParams(queryString || ""), [queryString]);

  const readShowMapFromQS = () => {
    const v = sp.get("show_map");
    if (v == null) return null; // Return null if no URL param exists
    const s = String(v).trim().toLowerCase();
    return s === "1" || s === "true" || s === "yes";
  };

  const [showMap, setShowMap] = useState(true);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [mapType, setMapType] = useState("roadmap"); // roadmap | satellite | hybrid | terrain
  const [mapBounds, setMapBounds] = useState(null); // { north, south, east, west }

  // Debounced map bounds change handler
  const boundsDebounceRef = useRef(null);
  const handleMapBoundsChange = useCallback((bounds) => {
    if (boundsDebounceRef.current) {
      clearTimeout(boundsDebounceRef.current);
    }
    boundsDebounceRef.current = setTimeout(() => {
      setMapBounds(bounds);
    }, 300); // 300ms debounce
  }, []);

  // Toggle map type (roadmap <-> satellite)
  const toggleMapStyle = () => {
    setMapType(prev => prev === "roadmap" ? "satellite" : "roadmap");
  };

  // Toggle map visibility from internal button
  const toggleMapVisibility = () => {
    const el = getHostEl();
    if (!el) return;
    const next = !showMap;
    el.setAttribute("data-map-state", String(next));
    setShowMap(next);
  };

  // Toggle map fullscreen
  const toggleMapFullscreen = () => {
    setMapFullscreen(prev => !prev);
  };

  // Elementor external button
  useEffect(() => {
    const wrap = document.getElementById("nexus_show_hide_map");
    if (!wrap) return;
    const btn   = wrap.querySelector("a.elementor-button");
    const label = wrap.querySelector(".elementor-button-text");
    if (!btn || !label) return;

    const setLabel = (showing) => { label.textContent = showing ? "Hide Map" : "Show Map"; };
    setLabel(showMap);

    const onClick = (e) => {
      e.preventDefault();
      const el = getHostEl();
      if (!el) return;

      // Determine if mobile/tablet or desktop (1024px breakpoint)
      const isMobile = window.innerWidth <= 1023;
      const attrName = isMobile ? "data-map-state-mobile" : "data-map-state";

      // Toggle the appropriate attribute
      const current = el.getAttribute(attrName) === "true";
      const next = !current;
      el.setAttribute(attrName, String(next));

      // Update URL with show_map parameter
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        url.searchParams.set("show_map", next ? "true" : "false");
        window.history.pushState({}, "", url.toString());
      }

      setLabel(next);
      setShowMap(next);
    };

    btn.addEventListener("click", onClick);
    return () => btn.removeEventListener("click", onClick);
  }, [showMap]);

  // watch URL changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    const win = window;
    const origPush = win.history.pushState;
    const origReplace = win.history.replaceState;
    const notify = () =>
      setQueryString((prev) =>
        prev !== win.location.search ? win.location.search : prev
      );
    const wrap = (fn) =>
      function (...args) {
        const ret = fn.apply(this, args);
        Promise.resolve().then(notify);
        return ret;
      };
    win.history.pushState = wrap(origPush);
    win.history.replaceState = wrap(origReplace);
    win.addEventListener("popstate", notify);
    return () => {
      win.history.pushState = origPush;
      win.history.replaceState = origReplace;
      win.removeEventListener("popstate", notify);
    };
  }, []);

  // reflect map visibility attrs (desktop & mobile)
  useEffect(() => {
    const el = getHostEl();
    if (!el) return;

    // Read from URL parameter (highest priority)
    const urlMapState = readShowMapFromQS();

    // Read existing attributes from saved settings
    const existingDesktop = el.getAttribute("data-map-state");
    const existingMobile = el.getAttribute("data-map-state-mobile");

    // Priority: URL param (if exists) > existing attribute > default (true)
    let desktopState, mobileState;

    if (urlMapState !== null) {
      // URL parameter exists - use it for both mobile and desktop
      desktopState = urlMapState;
      mobileState = urlMapState;
    } else {
      // No URL parameter - use existing attributes or default to true
      desktopState = existingDesktop !== null ? existingDesktop === "true" : true;
      mobileState = existingMobile !== null ? existingMobile === "true" : true;
    }

    // Set attributes with true/false values
    el.setAttribute("data-map-state", String(desktopState));
    el.setAttribute("data-map-state-mobile", String(mobileState));
    el.setAttribute("data-has-map", "true"); // Map is available

    // Determine current viewport state for React state (1024px breakpoint)
    const isMobile = window.innerWidth <= 1023;
    const currentState = isMobile ? mobileState : desktopState;
    setShowMap(currentState);

    // Watch for attribute changes (from account settings loading)
    const observer = new MutationObserver(() => {
      const updatedDesktop = el.getAttribute("data-map-state");
      const updatedMobile = el.getAttribute("data-map-state-mobile");

      // Re-check URL param in case it changed
      const latestUrlParam = readShowMapFromQS();

      if (latestUrlParam !== null) {
        // URL param exists - use it
        const isMobile = window.innerWidth <= 1023;
        setShowMap(latestUrlParam);
      } else {
        // Use updated attributes
        const isMobile = window.innerWidth <= 1023;
        const attrName = isMobile ? updatedMobile : updatedDesktop;
        if (attrName !== null) {
          setShowMap(attrName === "true");
        }
      }
    });

    observer.observe(el, {
      attributes: true,
      attributeFilter: ["data-map-state", "data-map-state-mobile"]
    });

    return () => observer.disconnect();
  }, [sp]);

  // 🔁 Provider/key fallback from host data-attrs
  useEffect(() => {
    const el = getHostEl();
    if (!el) return;
    const attrIsGoogle = el.getAttribute("data-is-google");
    const attrKey = (el.getAttribute("data-google-api-key") || "").trim();
    const resolvedIsGoogle = attrIsGoogle != null ? truthy(attrIsGoogle, false) : isGoogle;
    const resolvedKey = attrKey || googleApiKey || "";
    setIsGoogleLocal(resolvedIsGoogle);
    setGoogleApiKeyLocal(resolvedKey);
    try {
      el.setAttribute("data-map-provider", resolvedIsGoogle ? "google" : "leaflet");
      el.setAttribute("data-google-key-len", String(resolvedKey.length));
    } catch {}
  }, [isGoogle, googleApiKey]);

  // load saved style/settings
  useEffect(() => {
    (async () => {
      try {
        const host = getHostEl();
        const domAcct =
          host?.getAttribute("data-account-id") ||
          host?.getAttribute("data-account") ||
          "";
        const accountFromUrl = sp.get("account");
        const accountForStyle = accountFromUrl || domAcct || account;
        if (!accountForStyle) return;

        // Fetch account data directly from API (NO CACHING - always fresh)
        logger.log('[GridWidget] Fetching account data directly from API (no cache)');
        const accountRes = await fetch(`https://api.nexus.design/api/accounts/${accountForStyle}`);
        if (!accountRes.ok) {
          logger.warn('[GridWidget] Failed to fetch account data:', accountRes.status);
          return;
        }

        const json = await accountRes.json();
        if (!json) return;

        // Extract selected_card_amenities and selected_card_tags
        const cardAmenities = (json.selected_card_amenities || []).map(item => {
          if (item.amenity && typeof item.amenity === 'object') {
            return { ...item.amenity, order: item.order };
          }
          return item;
        });
        const cardTags = (json.selected_card_tags || []).map(item => {
          if (item.tag && typeof item.tag === 'object') {
            return { ...item.tag, order: item.order };
          }
          return item;
        });
        logger.log('[Widget] Setting card amenities:', cardAmenities);
        logger.log('[Widget] Setting card tags:', cardTags);
        setSelectedCardAmenities(cardAmenities);
        setSelectedCardTags(cardTags);

        let styleData = json?.grid_widget_style;

        try {
          const item = styleData?.item || {};
          setImagePref(normalizeImageSize(item.imageSize));
        } catch {}

        if (typeof styleData === "string") {
          try { styleData = JSON.parse(styleData); }
          catch { injectCss(styleData); return; }
        }

        try {
          const item = styleData?.item || {};
          const enabled = item.shadowEnabled;
          const value = item.shadow || "0 6px 20px rgba(2,6,23,.08)";
          setCardShadowCss(enabled ? value : "none");
        } catch {}

        if (styleData && typeof styleData === "object") {
          try {
            const el = getHostEl();
            const raw = styleData?.map?.mapTileStyle;
            const normalized = normalizeMapStyle(raw);
            if (normalized) {
              el?.setAttribute("data-map-tile-style", normalized);
              setMapTileStyle(normalized);
            } else {
              el?.removeAttribute("data-map-tile-style");
              setMapTileStyle(undefined);
            }
          } catch {}

          // Read cluster style
          try {
            const clusterData = styleData?.map?.clusterStyle;
            if (clusterData && typeof clusterData === "object") {
              setClusterStyle({
                bgColor: clusterData.bgColor || "#fe5922",
                textColor: clusterData.textColor || "#ffffff",
                borderColor: clusterData.borderColor || "#ffffff",
                borderWidth: clusterData.borderWidth || 3,
                fontSize: clusterData.fontSize || 14,
                fontWeight: clusterData.fontWeight || 700,
                size: clusterData.size || 40,
              });
            }
          } catch {}

          injectCss(buildCss(styleData));

          // Inject custom CSS if present
          const customCss = styleData?.widget?.customCss;
          if (customCss) {
            injectCustomCss(customCss);
          }

          const w = styleData.widget || {};
          const m = styleData.meta || {};
          setSavedTextMeta({
            pricePrefix: w.pricePrefix,
            priceSuffix: w.priceSuffix,
            pricePrefixDated: w.pricePrefixDated,
            priceSuffixDated: w.priceSuffixDated,
            openLinkNewTab: w.openLinkNewTab,
            showGuests: m.showGuests,
            showBedrooms: m.showBedrooms,
            showBathrooms: m.showBathrooms,
            useTextGuests: m.useTextGuests,
            useTextBedrooms: m.useTextBedrooms,
            useTextBathrooms: m.useTextBathrooms,
            iconGuests: m.iconGuests,
            iconBedrooms: m.iconBedrooms,
            iconBathrooms: m.iconBathrooms,
            singularPrefix: w.singularPrefix,
            singularSuffix: w.singularSuffix,
            pluralPrefix:   w.pluralPrefix,
            pluralSuffix:   w.pluralSuffix,
          });

          try {
            const el = getHostEl();
            if (el) {
              if (w.singularPrefix != null) el.setAttribute("data-singular-prefix", w.singularPrefix);
              if (w.singularSuffix != null) el.setAttribute("data-singular-suffix", w.singularSuffix);
              if (w.pluralPrefix   != null) el.setAttribute("data-plural-prefix",   w.pluralPrefix);
              if (w.pluralSuffix   != null) el.setAttribute("data-plural-suffix",   w.pluralSuffix);
            }

            const savedIsCarousel = !!styleData?.behavior?.carousel;
            if (el) {
              if (savedIsCarousel) el.setAttribute("data-is-carousel", "true");
              else el.removeAttribute("data-is-carousel");
            }
            setIsCarousel(savedIsCarousel);

            if (el && styleData.map) {
              const hasAnyIconAttr = ICON_ATTRS.some((a) => el.hasAttribute(a));
              if (!hasAnyIconAttr) {
                const spec = mapStyleToIconSpec(styleData.map);
                setIconSpec(spec);
                applyIconSpecToEl(el, spec);
              }

              // Apply Google Maps provider setting from saved settings
              const mapSettings = styleData.map;
              const savedIsGoogle = mapSettings.mapProvider === "google";
              const savedApiKey = mapSettings.googleApiKey || "";

              // Only apply if not already set via data attributes
              if (!el.hasAttribute("data-is-google")) {
                el.setAttribute("data-is-google", savedIsGoogle ? "true" : "false");
                setIsGoogleLocal(savedIsGoogle);
              }
              if (savedIsGoogle && savedApiKey && !el.hasAttribute("data-google-api-key")) {
                el.setAttribute("data-google-api-key", savedApiKey);
                setGoogleApiKeyLocal(savedApiKey);
              }

              logger.log('[Widget] Applied map provider from settings:', {
                provider: savedIsGoogle ? 'google' : 'leaflet',
                hasApiKey: !!savedApiKey
              });

              // Apply map height from saved settings
              if (mapSettings.mapHeightUnit === "calc" && mapSettings.mapHeightCalc) {
                setMapHeightLocal(mapSettings.mapHeightCalc);
                setMapHeightUnitLocal("calc");
              } else if (Number.isFinite(mapSettings.mapHeight) || mapSettings.mapHeightUnit === "vh") {
                setMapHeightLocal(mapSettings.mapHeight || 520);
                setMapHeightUnitLocal(mapSettings.mapHeightUnit || "px");
              }

              logger.log('[Widget] Applied map height from settings:', {
                height: mapSettings.mapHeight,
                unit: mapSettings.mapHeightUnit,
                calc: mapSettings.mapHeightCalc
              });

              // Apply responsive map state from saved settings (unless URL param exists)
              const urlMapParam = new URLSearchParams(window.location.search).get("show_map");

              if (urlMapParam === null) {
                // No URL parameter - apply saved settings
                // (mapSettings already defined above)

                // Read showOnMobile/showOnTablet/showOnDesktop booleans (new format)
                // Fallback to legacy mobileState/desktopState strings, then defaultState
                const showMobile = mapSettings.showOnMobile === true || mapSettings.mobileState === "showing";
                const showTablet = mapSettings.showOnTablet !== false || mapSettings.tabletState === "showing";
                const showDesktop = mapSettings.showOnDesktop !== false || mapSettings.desktopState === "showing" || mapSettings.defaultState === "showing";

                // Set data attributes for responsive map visibility
                el.setAttribute("data-map-show-mobile", String(showMobile));
                el.setAttribute("data-map-show-tablet", String(showTablet));
                el.setAttribute("data-map-show-desktop", String(showDesktop));
                // Legacy attributes
                el.setAttribute("data-map-state-mobile", String(showMobile));
                el.setAttribute("data-map-state", String(showDesktop));

                logger.log('[Widget] Applied responsive map states from settings:', {
                  mobile: showMobile,
                  tablet: showTablet,
                  desktop: showDesktop
                });
              } else {
                // URL parameter exists - it will be applied by the useEffect
                logger.log('[Widget] Skipping saved map states - URL parameter present:', urlMapParam);
              }
            }
          } catch {}

          // Load posts per page setting
          try {
            const widgetSettings = styleData?.widget || {};
            const savedPosts = widgetSettings.postsPerPage;
            if (Number.isFinite(savedPosts) && savedPosts > 0) {
              setSavedPerPage(savedPosts);
              logger.log('[Widget] Loaded posts per page from settings:', savedPosts);
            } else {
              logger.log('[Widget] No posts per page in settings, using default');
            }
          } catch {}

          // Load search bar settings from saved style
          try {
            const searchBarSettings = styleData?.searchBar || {};
            const el = getHostEl();

            if (searchBarSettings.enabled === true) {
              setSearchBarEnabledLocal(true);
              if (el) el.setAttribute("data-search-bar-enabled", "true");
              logger.log('[Widget] Search bar enabled from saved settings');
            }
            if (searchBarSettings.sticky === true) {
              setSearchBarStickyLocal(true);
              if (el) el.setAttribute("data-search-bar-sticky", "true");
            }
            if (Number.isFinite(searchBarSettings.padding)) {
              setSearchBarPaddingLocal(searchBarSettings.padding);
            }
            if (Number.isFinite(searchBarSettings.borderWidth)) {
              setSearchBarBorderWidthLocal(searchBarSettings.borderWidth);
            }
            if (searchBarSettings.borderColor) {
              setSearchBarBorderColorLocal(searchBarSettings.borderColor);
            }
          } catch {}

          // Load display options from saved style
          try {
            const widgetSettings = styleData?.widget || {};
            if (widgetSettings.lazyLoad === false) {
              setLazyLoadLocal(false);
            }
            if (widgetSettings.showTags === false) {
              setShowTagsLocal(false);
            }
            if (widgetSettings.showAmenities === false) {
              setShowAmenitiesLocal(false);
            }
          } catch {}
        }
      } catch (err) {
        logger.warn("Failed to load grid_widget_style", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]); // Removed queryString - account styles don't change based on URL params

  // Detect location/city from URL and fetch hero data
  useEffect(() => {
    const detectAndFetchLocation = async () => {
      if (!account) return;

      try {
        const pathname = window.location.pathname;
        const pathSegments = pathname.split("/").filter(Boolean);

        // Check for pattern: /location/{slug} or /city/{slug}
        if (pathSegments.length >= 2) {
          const type = pathSegments[0].toLowerCase();
          const slug = decodeURIComponent(pathSegments[1]);

          if ((type === "location" || type === "city") && slug) {
            setLocationLoading(true);

            const response = await fetch(
              `https://api.nexus.design/api/combined-cities/search/${account}?name=${slug}`
            );

            if (response.ok) {
              const data = await response.json();

              if (data.cities && data.cities.length > 0) {
                setLocationData(data.cities[0]);
                logger.log('[GridWidget] Location hero data loaded:', data.cities[0]);
              }
            }

            setLocationLoading(false);
          }
        }
      } catch (error) {
        logger.error('[GridWidget] Error fetching location data:', error);
        setLocationLoading(false);
      }
    };

    detectAndFetchLocation();
  }, [account]);

  /* 🔁 Observe data-is-carousel + icon attrs */
  const [isCarousel, setIsCarousel] = useState(false);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const el = getHostEl();
    if (!el) return;

    const readBool = (v) => String(v).toLowerCase() === "true";
    const readFlag = () =>
      setIsCarousel(readBool(el.getAttribute("data-is-carousel")));
    const readIconSpec = () => {
      const mode = (el.getAttribute("data-map-icon-mode") || "fa").toLowerCase();
      const faFamily = el.getAttribute("data-map-icon-fa-family") || "fa-solid";
      const faIcon = el.getAttribute("data-map-icon-fa-icon") || "fa-location-dot";
      const sizeAttr = el.getAttribute("data-map-icon-size");
      const size = Number.isFinite(parseInt(sizeAttr, 10)) ? parseInt(sizeAttr, 10) : 28;
      const color = el.getAttribute("data-map-icon-color") || "#fe5922";
      const svg = el.getAttribute("data-map-icon-svg") || "";
      setIconSpec({ mode: mode === "svg" ? "svg" : "fa", faFamily, faIcon, size, color, svg });
    };

    readFlag();
    readIconSpec();

    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type !== "attributes") continue;
        if (m.attributeName === "data-is-carousel") readFlag();
        if (
          m.attributeName === "data-map-icon-mode" ||
          m.attributeName === "data-map-icon-fa-family" ||
          m.attributeName === "data-map-icon-fa-icon" ||
          m.attributeName === "data-map-icon-size" ||
          m.attributeName === "data-map-icon-color" ||
          m.attributeName === "data-map-icon-svg"
        ) readIconSpec();
      }
    });
    mo.observe(el, { attributes: true });
    return () => mo.disconnect();
  }, []);

  // Load embedded search widget
  useEffect(() => {
    if (!searchBarEnabledLocal) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      // Check if search widget script is already loaded
      if (window.NexusSearchBundle || window.NexusSearchReload) {
        setSearchBarLoaded(true);
        // Initialize/reload search widget in the container
        if (window.NexusSearchReload) {
          window.NexusSearchReload();
        }
        return;
      }

      // Check if script is already in DOM
      if (document.querySelector('script[src*="nexus-search"]')) {
        // Script exists but may still be loading
        return;
      }

      // Load the search widget script
      const script = document.createElement("script");
      script.src = searchBarScriptUrl;
      script.async = true;
      script.onload = () => {
        logger.log("[GridWidget] Search widget script loaded");
        setSearchBarLoaded(true);
        // Trigger reload to mount on our container
        if (window.NexusSearchReload) {
          window.NexusSearchReload();
        }
      };
      script.onerror = () => {
        logger.warn("[GridWidget] Failed to load search widget script:", searchBarScriptUrl);
      };
      document.head.appendChild(script);
    }, 100);

    return () => clearTimeout(timer);
  }, [searchBarEnabledLocal, searchBarScriptUrl]);

  const palette = useMemo(
    () => ({
      border: "rgba(17,24,39,.12)",
      shadow: cardShadowCss,
      badge: "#0ea5e9",
    }),
    [cardShadowCss]
  );

  // URL helpers
  const parseCsv = (k) =>
    (sp.get(k) || "").split(",").map((s) => s.trim()).filter(Boolean);
  const parseIntParam = (k, d) => {
    const v = sp.get(k);
    const n = v == null ? NaN : parseInt(v, 10);
    return Number.isFinite(n) ? n : d;
  };
  const parseBool = (k, d = false) => {
    const v = sp.get(k);
    if (v == null) return d;
    const s = String(v).toLowerCase();
    return s === "1" || s === "true" || s === "yes";
  };

  // ✅ Parse URL path for filters (supports singular & plural: /amenity, /amenities, /tag, /tags, /state, /country, /complex, /type, etc.)
  const parseUrlPathFilters = () => {
    const pathFilters = { cities: [], tags: [], amenities: [], categories: [], states: [], countries: [], complexes: [], types: [] };
    try {
      const pathname = window.location.pathname;
      // Match patterns like /location/value, /tag/value, /amenity/value, /category/value (singular or plural)
      const pathSegments = pathname.split("/").filter(Boolean);

      // Check for two segments: /{type}/{value}
      if (pathSegments.length >= 2) {
        const type = pathSegments[0].toLowerCase();
        const value = decodeURIComponent(pathSegments[1]);

        if ((type === "location" || type === "locations" || type === "city" || type === "cities") && value) {
          pathFilters.cities = [value];
        } else if ((type === "tag" || type === "tags") && value) {
          pathFilters.tags = [value];
        } else if ((type === "amenity" || type === "amenities") && value) {
          pathFilters.amenities = [value];
        } else if ((type === "category" || type === "categories") && value) {
          pathFilters.categories = [value];
        } else if ((type === "state" || type === "states") && value) {
          pathFilters.states = [value];
        } else if ((type === "country" || type === "countries") && value) {
          pathFilters.countries = [value];
        } else if ((type === "complex" || type === "complexes") && value) {
          pathFilters.complexes = [value];
        } else if ((type === "type" || type === "types") && value) {
          pathFilters.types = [value];
        }
      }
    } catch (e) {
      logger.warn("[Grid Widget] Error parsing URL path filters:", e);
    }
    return pathFilters;
  };
  const urlPathFilters = parseUrlPathFilters();

  const startDate = sp.get("startDate") ?? "";
  const endDate = sp.get("endDate") ?? "";
  const accountId = sp.get("account") || account;
  const urlPage = parseIntParam("page", 1);
  const urlPerPage = parseIntParam("per_page", savedPerPage || perPage);
  const priceMin = parseIntParam("price_min", 0);
  const priceMax = parseIntParam("price_max", 0);
  const flexibilityDaysParam = parseIntParam("flexibilityDays", 0);
  const bedsParam = parseIntParam("beds", 0);

  // Extract all filter params (prevents refresh when non-filter params like show_map change)
  // Merge with URL path filters (path filters take precedence if query params are empty)
  const amenitiesParam = parseCsv("amenities").join(",") || urlPathFilters.amenities.join(",");
  const citiesParam = parseCsv("location").join(",") || urlPathFilters.cities.join(",");
  const isFeaturedParam = parseBool("is_featured", false);
  const tagsParam = parseCsv("tags").join(",") || urlPathFilters.tags.join(",");
  const categoriesParam = parseCsv("categories").join(",") || urlPathFilters.categories.join(",");
  const typeParam = parseCsv("type").join(",") || urlPathFilters.types.join(",");
  const statesParam = parseCsv("state").join(",") || urlPathFilters.states.join(",");
  const countriesParam = parseCsv("country").join(",") || urlPathFilters.countries.join(",");
  const complexesParam = parseCsv("complex").join(",") || urlPathFilters.complexes.join(",");
  const bedroomsParam = parseIntParam("bedrooms", 0);
  const bathroomsParam = parseIntParam("bathrooms", 0);
  const guestsParam = parseIntParam("guests", 1);
  const infantsParam = parseIntParam("infants", 0);
  const childrenParam = parseIntParam("children", 0);
  const petsParam = parseIntParam("pets", 0);
  const adultsParam = parseIntParam("adults", 1);
  const sortParam = sp.get("sort") || "";

  const payload = useMemo(() => {
    const filter = {
      amenities: amenitiesParam ? amenitiesParam.split(",") : [],
      cities: citiesParam ? citiesParam.split(",") : [],
      is_featured: isFeaturedParam,
      tags: tagsParam ? tagsParam.split(",") : [],
      categories: categoriesParam ? categoriesParam.split(",") : [],
      type: typeParam ? typeParam.split(",") : [],
      states: statesParam ? statesParam.split(",") : [],
      countries: countriesParam ? countriesParam.split(",") : [],
      complexes: complexesParam ? complexesParam.split(",") : [],
      beds: bedsParam,
      bedrooms: bedroomsParam,
      bathrooms: bathroomsParam,
      guests: guestsParam,
      infants: infantsParam,
      children: childrenParam,
      pets: petsParam,
      adults: adultsParam,
    };
    Object.keys(filter).forEach((k) => {
      const v = filter[k];
      const emptyArr = Array.isArray(v) && v.length === 0;
      const zeroNum = typeof v === "number" && v === 0;
      // Don't send guests=1 or adults=1 as filters - these are "no filter" defaults
      const defaultGuestFilter = (k === "guests" || k === "adults") && v === 1;
      if (v === undefined || v === null || emptyArr || zeroNum || defaultGuestFilter) delete filter[k];
    });

    const base = {
      startDate,
      endDate,
      account: accountId,
      noDate: "average_nightly",
      withDate: "calculated_total",
      page: urlPage,
      posts_per_page: urlPerPage,
      current_query: currentQuery,
      ...(Object.keys(filter).length ? { filter } : {}),
    };
    if (priceMin > 0) base.price_min = priceMin;
    if (priceMax > 0) base.price_max = priceMax;
    if (flexibilityDaysParam > 0) base.flexibilityDays = flexibilityDaysParam;
    if (sortParam) base.sort = sortParam;
    return base;
  }, [startDate, endDate, accountId, urlPage, urlPerPage, bedsParam, priceMin, priceMax, flexibilityDaysParam, amenitiesParam, citiesParam, isFeaturedParam, tagsParam, categoriesParam, typeParam, statesParam, countriesParam, complexesParam, bedroomsParam, bathroomsParam, guestsParam, infantsParam, childrenParam, petsParam, adultsParam, currentQuery, sortParam]);

  /* Data fetch */
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pageInfo, setPageInfo] = useState({
    page: urlPage,
    totalPages: 1,
    total: 0,
    perPage: urlPerPage,
  });

  // Filter items based on current map bounds (client-side) - only when map is visible
  const filteredItems = useMemo(() => {
    // Only filter by bounds if map is visible and we have bounds
    if (!mapBounds || !items.length || !map || !showMap) return items;

    const { north, south, east, west } = mapBounds;

    // Check if bounds are valid (not default/empty)
    if (!Number.isFinite(north) || !Number.isFinite(south) ||
        !Number.isFinite(east) || !Number.isFinite(west)) {
      return items;
    }

    const filtered = items.filter(item => {
      // Include items without coordinates (they just won't be on map)
      if (!Number.isFinite(item.lat) || !Number.isFinite(item.lng)) {
        return true;
      }
      const inLatRange = item.lat >= south && item.lat <= north;
      const inLngRange = item.lng >= west && item.lng <= east;
      return inLatRange && inLngRange;
    });

    // If filtering removes all items, return original (bounds might be wrong)
    return filtered.length > 0 ? filtered : items;
  }, [items, mapBounds, map, showMap]);

  // Track if the map is currently filtering results
  const isFilteredByMap = useMemo(() => {
    if (!mapBounds || !items.length || !map || !showMap) return false;
    const { north, south, east, west } = mapBounds;
    if (!Number.isFinite(north) || !Number.isFinite(south) ||
        !Number.isFinite(east) || !Number.isFinite(west)) {
      return false;
    }
    // Return true if some items were filtered out
    return filteredItems.length < items.length;
  }, [items, filteredItems, mapBounds, map, showMap]);

  const httpsify = (u) =>
    typeof u === "string" ? u.replace(/^http:\/\//i, "https://") : "";
  const debounceTimer = useRef(null);
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setLoading(true);
    setError("");
    setItems([]);

    debounceTimer.current = setTimeout(async () => {
      try {
        logger.log('[nexus-grid] API payload:', JSON.stringify(payload, null, 2));
        const res = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const normalized = Array.isArray(data?.items)
          ? data.items.map((it, idx) => {
              const pics = Array.isArray(it?.pictures)
                ? it.pictures
                    .map((p) => httpsify(pickImageByPref(p, imagePref)))
                    .filter(Boolean)
                    .slice(0, 5)
                : [];
              const firstPic = pics[0] || "";
              const lat = Number(it?.geoLocation?.latitude);
              const lng = Number(it?.geoLocation?.longitude);
              const title = it?.listingName || `Listing ${idx + 1}`;

              const _raw = Number(String(it.total).replace(/[^0-9.-]/g, ""));
              const priceWhole = Number.isFinite(_raw) ? Math.ceil(_raw) : null;
              const priceDisplay = priceWhole !== null ? priceWhole.toLocaleString() : it.total;

              const priceText =
                it?.total != null ? `${it?.currency ?? ""} ${String(it.total)}` : "";
              return {
                id: it?.listingId ?? it?.id ?? idx,
                title,
                city: it?.city,
                state: it?.state,
                images: pics,
                image: firstPic,
                price: priceText && priceText.trim()
                  ? `${it?.currency ? currencySymbol(it?.currency) : ""}${priceDisplay}`
                  : "",
                currency: it?.currency,
                lat: Number.isFinite(lat) ? lat : undefined,
                lng: Number.isFinite(lng) ? lng : undefined,
                href: it?.slug ? `/listing/${it.slug}` : undefined,
                isFeatured: !!it?.is_featured,
                guests: it?.guests,
                bedrooms: it?.bedrooms,
                bathrooms: it?.bathrooms,
                nexus_listing_url_slug: it?.nexus_listing_url_slug,
                amenities: it?.amenities || [],
                tags: it?.tags || [],
              };
            })
          : [];

        const p = data?.pagination || {};
        const pageFromApi = Number(p.page);
        const totalPagesFromApi = Number(p.total_pages);
        const totalFromApi = Number(p.total);
        const pageFromPhp = Number(data?.current_page);
        const totalPagesFromPhp = Number(data?.max_pages);
        const totalFromPhp = Number(data?.total_results);

        const finalTotal = totalFromApi || totalFromPhp || 0;
        logger.log('[nexus-grid] Pagination data:', {
          pagination: p,
          totalFromApi,
          totalFromPhp,
          totalPagesFromApi,
          totalPagesFromPhp,
          itemsReceived: normalized.length,
          finalTotal,
          rawData: data
        });

        setPageInfo({
          page: pageFromApi || pageFromPhp || urlPage || 1,
          totalPages: totalPagesFromApi || totalPagesFromPhp || 1,
          total: finalTotal,
          perPage: p.per_page || urlPerPage || perPage,
        });

        setItems(normalized);
        setLoading(false);
      } catch (e) {
        setError(e?.message || "Failed to load");
        setLoading(false);
        setPageInfo((old) => ({ ...old, totalPages: 1, total: 0 }));
      }
    }, Math.max(0, debounceMs));

    return () => debounceTimer.current && clearTimeout(debounceTimer.current);
  }, [apiUrl, authToken, payload, debounceMs, urlPage, urlPerPage, perPage, imagePref]);

  /* responsive columns */
  const [vw, setVw] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // map on/off
  const hasMap = map && showMap;

  // Determine if map is actually visible at current viewport
  // Map visibility depends on device breakpoints and settings
  // Use previewWidth if in dashboard preview mode, otherwise use actual viewport
  const mapVisibleAtViewport = useMemo(() => {
    if (!map || !showMap) return false;

    // In preview mode, use the preview width setting
    if (previewWidth && previewWidth !== '100%') {
      const pw = parseInt(previewWidth, 10);
      if (pw <= 375) return mapShowMobile;   // Mobile preview
      if (pw <= 768) return mapShowTablet;   // Tablet preview
      return mapShowDesktop;                  // Desktop preview
    }

    // Normal mode - use actual viewport
    if (vw < 640) return mapShowMobile;  // Mobile
    if (vw < 1024) return mapShowTablet; // Tablet
    return mapShowDesktop;               // Desktop
  }, [map, showMap, vw, previewWidth, mapShowMobile, mapShowTablet, mapShowDesktop]);

  // Choose columns based on whether map is actually visible at this viewport
  const effectiveColumnsRaw = useMemo(() => {
    if (mapVisibleAtViewport && columnsWithMap) {
      return columnsWithMap;
    }
    if (columnsNoMap) {
      return columnsNoMap;
    }
    return columns;
  }, [mapVisibleAtViewport, columnsWithMap, columnsNoMap, columns]);

  const listColumns = useMemo(() => {
    // Use the actual column values from settings - base/sm are mobile columns
    const base = effectiveColumnsRaw?.base ?? effectiveColumnsRaw?.sm ?? 1;
    return {
      base,
      sm: effectiveColumnsRaw?.sm ?? base,
      md: effectiveColumnsRaw?.md ?? 3,
      lg: effectiveColumnsRaw?.lg ?? 4,
      xl: effectiveColumnsRaw?.xl ?? (effectiveColumnsRaw?.lg ?? 4),
    };
  }, [effectiveColumnsRaw]);

  // write CSS vars
  useEffect(() => {
    const host = getHostEl();
    if (!host || !listColumns) return;
    host.style.setProperty("--nxw-cols-base", String(listColumns.base ?? 2));
    host.style.setProperty("--nxw-cols-sm",   String(listColumns.sm ?? listColumns.base ?? 2));
    host.style.setProperty("--nxw-cols-md",   String(listColumns.md ?? 3));
    host.style.setProperty("--nxw-cols-lg",   String(listColumns.lg ?? 4));
    host.style.setProperty("--nxw-cols-xl",   String(listColumns.xl ?? (listColumns.lg ?? 4)));
    host.style.setProperty("--nxw-cols-xxl",  String(listColumns.xl ?? (listColumns.lg ?? 4)));
  }, [listColumns]);

  /* map/list split CSS - with sticky map */
  const mapHeightWithUnit = mapHeightUnitLocal === "calc" ? `calc(${mapHeightLocal})` : `${mapHeightLocal}${mapHeightUnitLocal}`;
  const layoutCSS = `
    .nexus-grid-widget-inner{
      min-height:auto!important;
      display:block;
      position:relative;
    }

    /* Pagination always below content with clear spacing */
    .nxp-wrap{
      width:100%;
      clear:both;
      margin-top:${gap * 2}px;
      padding-top:${gap}px;
      position:relative;
      z-index:2;
    }

    /* Pagination inside ww-list when map is showing */
    .ww-list > .nxp-wrap{
      margin-bottom:${gap}px;
    }

    /* Device-specific map visibility */
    ${!mapShowMobile ? `@media (max-width: 639.98px) { .ww-map { display: none !important; } .ww-list { flex: 1 1 100% !important; } }` : ''}
    ${!mapShowTablet ? `@media (min-width: 640px) and (max-width: 1023.98px) { .ww-map { display: none !important; } .ww-list { flex: 1 1 100% !important; } }` : ''}
    ${!mapShowDesktop ? `@media (min-width: 1024px) { .ww-map { display: none !important; } .ww-list { flex: 1 1 100% !important; } }` : ''}

    @media (max-width: 767.98px){
      .ww-responsive{display:flex;flex-direction:column;gap:${gap}px;margin-bottom:0;}
      .ww-map{order:1;max-height:50vh;border-radius:12px;overflow:hidden;}
      .ww-map .nxm-wrap{height:100%!important;}
      .ww-list{order:2;}
    }
    @media (min-width: 768px){
      .ww-responsive{
        display:flex;
        gap:${gap}px;
        align-items:stretch;
        position:relative;
        margin-bottom:${gap}px;
      }
      .ww-list{
        order:1;
        flex:1 1 0;
        min-width:0;
        padding:8px;
        padding-bottom:${gap}px;
        margin:-8px;
        margin-bottom:0;
        overflow-y:auto;
        max-height:${mapHeightWithUnit};
        scrollbar-width:none;
        -ms-overflow-style:none;
      }
      .ww-list::-webkit-scrollbar{display:none;}
      .ww-map{
        order:2;
        flex:0 0 50%;
        min-width:0;
        height:${mapHeightWithUnit};
        position:sticky;
        top:10px;
        align-self:flex-start;
        border-radius:12px;
        overflow:hidden;
      }
      .ww-map .nxm-wrap,
      .ww-map .nxm-google-map-wrap{
        height:100%!important;
        border-radius:12px;
        overflow:hidden;
      }
      .ww-map .nxm-map,
      .ww-map .nxm-google-map,
      .ww-map .leaflet-container{
        height:100%!important;
        border-radius:12px;
      }
      .ww-map .nxw-map-controls{
        position:absolute;
        top:10px;
        right:10px;
        z-index:99999;
      }
    }`;

  const wrap = {
    background: "transparent",
    color: "#111827",
    padding: 10,
  };

  const setPageInUrl = (newPage) => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("page", String(newPage));
    if (!url.searchParams.get("per_page") && pageInfo.perPage) {
      url.searchParams.set("per_page", String(pageInfo.perPage));
    }
    window.history.pushState({}, "", url.toString());
  };

  const setSortInUrl = (newSort) => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (newSort) {
      url.searchParams.set("sort", newSort);
    } else {
      url.searchParams.delete("sort");
    }
    // Reset to page 1 when changing sort
    url.searchParams.set("page", "1");
    window.history.pushState({}, "", url.toString());
  };

  const [activeId, setActiveId] = useState(null);
  const listBoxRef = useRef(null);

  // Scroll to card using imported helpers
  const scrollToCard = (id) => {
    const box = listBoxRef.current;
    const el = document.getElementById(`nexus-card-${id}`);
    logger.log('[GridWidget] scrollToCard called:', { id, hasBox: !!box, hasEl: !!el, elId: `nexus-card-${id}` });
    if (!box || !el) {
      logger.warn('[GridWidget] scrollToCard aborted - missing:', { box: !!box, el: !!el });
      return;
    }

    // position of the card inside the scroll container
    const elTopInside = offsetTopRelative(el, box);

    // center the card in the viewport of the list
    const target = elTopInside - (box.clientHeight / 2 - el.clientHeight / 2);

    // animate our container only (no native smooth)
    animateScroll(box, target, 380);

    // brief highlight with outline (more visible than box-shadow)
    el.classList.add("nx-pulse");
    logger.log('[GridWidget] Added nx-pulse class to card:', el.id);
    setTimeout(() => el.classList.remove("nx-pulse"), 1200);
  };



  useEffect(() => {
    if (document.getElementById("__nx_pulse_css")) return;
    const s = document.createElement("style");
    s.id = "__nx_pulse_css";
    s.textContent = `
      .nx-pulse{
        position:relative!important;
        z-index:10!important;
        overflow:hidden!important;
      }
      .nx-pulse::after{
        content:""!important;
        position:absolute!important;
        inset:0!important;
        border:3px solid rgba(254,89,34,.9)!important;
        border-radius:inherit!important;
        box-shadow:0 0 0 6px rgba(254,89,34,.25)!important;
        pointer-events:none!important;
        z-index:9999!important;
      }
    `;
    document.head.appendChild(s);
  }, []);

  const itemsById = useMemo(() => {
    const map = new Map();
    for (const it of items) map.set(it.id, it);
    return map;
  }, [items]);

  const safeSetActiveId = useCallback((id) => {
    if (id == null) return setActiveId(null);
    const it = itemsById.get(id);
    if (it && Number.isFinite(it.lat) && Number.isFinite(it.lng)) {
      setActiveId(id);
    } else {
      setActiveId(null);
    }
  }, [itemsById]);

  // Memoized pin click handler to prevent marker recreation
  const handlePinClick = useCallback((id) => {
    logger.log('[GridWidget] handlePinClick called with id:', id);
    const it = itemsById.get(id);
    logger.log('[GridWidget] Found item:', it ? { id: it.id, lat: it.lat, lng: it.lng } : null);
    if (it && Number.isFinite(it.lat) && Number.isFinite(it.lng)) {
      safeSetActiveId(id);
      // ensure DOM has updated before we scroll
      requestAnimationFrame(() => scrollToCard(id));
    } else {
      logger.warn('[GridWidget] handlePinClick - item not found or missing coords');
      safeSetActiveId(null);
    }
  }, [itemsById, safeSetActiveId]);

  // final provider decision (fallback to Leaflet if key missing)
  const useGoogleMaps = isGoogleLocal && !!googleApiKeyLocal;

  // Build location/city hero section
  const locationHeroSection = locationData ? (
    <div
      className="nxw-location-hero"
      style={{
        width: '100%',
        marginBottom: '40px',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        alignItems: 'center',
      }}
    >
      <style>{`
        @media (min-width: 768px) {
          .nxw-location-hero {
            grid-template-columns: 1fr 1fr !important;
          }
        }
      `}</style>

      {/* Left side - Text content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: '36px',
            fontWeight: 700,
            lineHeight: 1.2,
            color: '#111827',
          }}
        >
          {locationData.name || locationData.title || 'Location'}
        </h1>
        {locationData.description && (
          <p
            style={{
              margin: 0,
              fontSize: '16px',
              lineHeight: 1.6,
              color: '#4b5563',
            }}
          >
            {locationData.description}
          </p>
        )}
      </div>

      {/* Right side - Image */}
      {locationData.image_url && (
        <div
          style={{
            width: '100%',
            height: '400px',
            borderRadius: '12px',
            overflow: 'hidden',
            order: -1, // Show image first on mobile
          }}
        >
          <style>{`
            @media (min-width: 768px) {
              .nxw-location-hero > div:last-child {
                order: 0 !important;
              }
            }
          `}</style>
          <img
            src={locationData.image_url}
            alt={locationData.name || locationData.title || 'Location'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </div>
      )}
    </div>
  ) : null;

  // Build search bar container element with embedded search widget style overrides
  const searchBarContainer = searchBarEnabledLocal ? (
    <div
      ref={searchBarRef}
      className={`nxw-search-bar-container${searchBarStickyLocal ? ' nxw-search-bar-sticky' : ''}`}
      style={{
        width: '100%',
        marginBottom: '16px',
        ...(searchBarStickyLocal ? {
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'var(--nxw-bg, #fff)',
        } : {})
      }}
    >
      {/* Override embedded search widget border/padding and fix responsive layout */}
      <style>{`
        .nxw-search-bar-container .nexus-search-widget[data-nexus-search] {
          padding: ${searchBarPaddingLocal}px !important;
          border: ${searchBarBorderWidthLocal > 0 ? `${searchBarBorderWidthLocal}px solid ${searchBarBorderColorLocal}` : 'none'} !important;
          border-radius: ${searchBarBorderWidthLocal > 0 ? '8px' : '0'} !important;
          flex-wrap: wrap !important;
        }
        .nxw-search-bar-container .nexus-search-widget[data-nexus-search] > div[data-type="input"] {
          min-width: 120px !important;
        }
        .nxw-search-bar-container .nexus-search-widget[data-nexus-search] > div[data-type="button"] {
          min-width: 80px !important;
        }
      `}</style>
      {/* Search widget mount point */}
      <div
        className="nexus-search-widget"
        data-account-id={account}
        data-embedded="true"
      />
    </div>
  ) : null;

  return (
    <div
      className="nexus-grid-widget-inner"
      style={wrap}
      data-singular-prefix={savedTextMeta.singularPrefix || ""}
      data-singular-suffix={savedTextMeta.singularSuffix || ""}
      data-plural-prefix={savedTextMeta.pluralPrefix || ""}
      data-plural-suffix={savedTextMeta.pluralSuffix || ""}
    >
      {map && showMap ? (
        <>
          <style>{`
            ${layoutCSS}
            .ww-list{scroll-behavior:auto !important; will-change: scroll-position;}
          `}</style>
          <div className={`ww-responsive${mapFullscreen ? ' ww-map-expanded' : ''}`}>
            <div className="ww-list" ref={listBoxRef} style={mapFullscreen ? { display: 'none' } : undefined}>
              {/* Location/City Hero Section */}
              {locationHeroSection}
              {/* Embedded Search Bar */}
              {searchBarContainer}
              {items.length > 0 ? (
                <ListGrid
                  items={filteredItems}
                  loading={loading}
                  error={error}
                  perPage={pageInfo.perPage || perPage}
                  totalCount={pageInfo.total}
                  look={{ gap, radius, columns: listColumns, palette }}
                  isCarousel={isCarousel}
                  activeId={activeId}
                  onItemHover={safeSetActiveId}
                  textMeta={savedTextMeta}
                  onItemLeave={() => setActiveId(null)}
                  selectedCardAmenities={selectedCardAmenities}
                  selectedCardTags={selectedCardTags}
                  lazyLoad={lazyLoadLocal}
                  showTags={showTagsLocal}
                  showAmenities={showAmenitiesLocal}
                  sortValue={sortParam}
                  onSortChange={setSortInUrl}
                  mapHidden={!showMap}
                  onShowMap={toggleMapVisibility}
                  linkTarget={linkTarget}
                  showGuests={showGuests}
                  showBedrooms={showBedrooms}
                  showBathrooms={showBathrooms}
                  showLocation={showLocation}
                  singularPrefix={singularPrefix}
                  singularSuffix={singularSuffix}
                  pluralPrefix={pluralPrefix}
                  pluralSuffix={pluralSuffix}
                  isFilteredByMap={isFilteredByMap}
                  virtualScroll={virtualScroll}
                />
              ) : loading ? (
                <ListGrid
                  items={[]}
                  loading={true}
                  error=""
                  perPage={pageInfo.perPage || perPage}
                  totalCount={0}
                  look={{ gap, radius, columns: listColumns, palette }}
                  isCarousel={isCarousel}
                  selectedCardAmenities={selectedCardAmenities}
                  selectedCardTags={selectedCardTags}
                  lazyLoad={lazyLoadLocal}
                  showTags={showTagsLocal}
                  showAmenities={showAmenitiesLocal}
                  sortValue={sortParam}
                  onSortChange={setSortInUrl}
                  mapHidden={!showMap}
                  onShowMap={toggleMapVisibility}
                  linkTarget={linkTarget}
                  showGuests={showGuests}
                  showBedrooms={showBedrooms}
                  showBathrooms={showBathrooms}
                  showLocation={showLocation}
                  singularPrefix={singularPrefix}
                  singularSuffix={singularSuffix}
                  pluralPrefix={pluralPrefix}
                  pluralSuffix={pluralSuffix}
                  virtualScroll={virtualScroll}
                />
              ) : (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  No listings found.
                </div>
              )}

              <Pagination
                page={pageInfo.page}
                totalPages={pageInfo.totalPages}
                loading={loading}
                onPageChange={setPageInUrl}
              />
            </div>

            <div className={`ww-map${mapFullscreen ? ' ww-map-fullscreen' : ''}`} style={mapFullscreen ? { position: 'relative', width: '100%', height: '80vh', flex: '1 1 100%' } : undefined}>
              {/* Map Control Buttons */}
              <div className="nxw-map-controls" style={{ position: 'absolute', top: 10, right: 10, zIndex: 10000, display: 'flex', flexDirection: 'row', gap: 6 }}>
                <button
                  className="nxw-map-ctrl-btn"
                  onClick={toggleMapStyle}
                  title={mapType === "satellite" ? "Switch to street map" : "Switch to satellite"}
                  aria-label={mapType === "satellite" ? "Switch to street map" : "Switch to satellite"}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: '#fff',
                    border: 'none',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#333',
                    padding: 0
                  }}
                >
                  {mapType === "satellite" ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                      <line x1="8" y1="2" x2="8" y2="18"></line>
                      <line x1="16" y1="6" x2="16" y2="22"></line>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="2" y1="12" x2="22" y2="12"></line>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                    </svg>
                  )}
                </button>
                <button
                  className="nxw-map-ctrl-btn"
                  onClick={toggleMapFullscreen}
                  title={mapFullscreen ? "Exit fullscreen" : "Fullscreen"}
                  aria-label={mapFullscreen ? "Exit fullscreen" : "Fullscreen"}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: '#fff',
                    border: 'none',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#333',
                    padding: 0
                  }}
                >
                  {mapFullscreen ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="4 14 10 14 10 20"></polyline>
                      <polyline points="20 10 14 10 14 4"></polyline>
                      <polyline points="14 20 14 14 20 14"></polyline>
                      <polyline points="4 10 10 10 10 4"></polyline>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <polyline points="9 21 3 21 3 15"></polyline>
                      <polyline points="3 9 3 3 9 3"></polyline>
                      <polyline points="21 15 21 21 15 21"></polyline>
                    </svg>
                  )}
                </button>
                <button
                  className="nxw-map-ctrl-btn"
                  onClick={toggleMapVisibility}
                  title="Close map"
                  aria-label="Close map"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: '#fff',
                    border: 'none',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#333',
                    padding: 0
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              {useGoogleMaps ? (
                <div className="nxm-google-map-wrap" style={{ height: mapFullscreen ? "100vh" : mapHeightWithUnit, width: '100%' }}>
                  <GoogleMapView
                    key="google-map"
                    items={items}
                    height="100%"
                    activeId={activeId}
                    visible={true}
                    apiKey={googleApiKeyLocal}
                    iconSpec={iconSpec}
                    isPricePins={isPricePins}
                    enableClustering={enableClustering}
                    clusterStyle={clusterStyle}
                    customSvgUrl={customSvgUrl}
                    onPinClick={handlePinClick}
                    onBoundsChange={handleMapBoundsChange}
                    mapType={mapType}
                    scrollWheel={mapWheel}
                    defaultZoom={mapZoom}
                    defaultCenter={mapCenter}
                  />
                </div>
              ) : (
                <MapView
                  key={`leaflet-map-${mapType}`}
                  items={items}
                  tileStyleProp={mapType === "satellite" ? "satellite" : mapTileStyle}
                  height={mapFullscreen ? "100vh" : mapHeightWithUnit}
                  activeId={activeId}
                  visible={true}
                  enableClustering={enableClustering}
                  clusterStyle={clusterStyle}
                  onPinClick={handlePinClick}
                  iconSpec={iconSpec}
                  onBoundsChange={handleMapBoundsChange}
                  scrollWheelZoom={mapWheel}
                  defaultZoom={mapZoom}
                  defaultCenter={mapCenter}
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Location/City Hero Section */}
          {locationHeroSection}
          {/* Embedded Search Bar */}
          {searchBarContainer}
          <ListGrid
            items={items}
            loading={loading}
            error={error}
            perPage={pageInfo.perPage || perPage}
            totalCount={pageInfo.total}
            look={{ gap, radius, columns: listColumns, palette }}
            isCarousel={isCarousel}
            onItemHover={safeSetActiveId}
            onItemLeave={() => setActiveId(null)}
            selectedCardAmenities={selectedCardAmenities}
            selectedCardTags={selectedCardTags}
            lazyLoad={lazyLoadLocal}
            showTags={showTagsLocal}
            showAmenities={showAmenitiesLocal}
            sortValue={sortParam}
            onSortChange={setSortInUrl}
            mapHidden={map && !showMap}
            onShowMap={toggleMapVisibility}
            linkTarget={linkTarget}
            showGuests={showGuests}
            showBedrooms={showBedrooms}
            showBathrooms={showBathrooms}
            showLocation={showLocation}
            singularPrefix={singularPrefix}
            singularSuffix={singularSuffix}
            pluralPrefix={pluralPrefix}
            pluralSuffix={pluralSuffix}
            isFilteredByMap={false}
            virtualScroll={virtualScroll}
          />

          <Pagination
            page={pageInfo.page}
            totalPages={pageInfo.totalPages}
            loading={loading}
            onPageChange={setPageInUrl}
          />
        </>
      )}
    </div>
  );
}